/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
import { POLL_INTERVAL_MS, hlsMetadataUrl, liveWsUrl } from "./config";
import type { TelemetrySource } from "../lib/url";

/** How long the in-band source waits for its first ID3 sample (with video up)
 *  before falling back to DATERANGE — i.e. concluding this logger has no
 *  injector. Generous enough to clear video startup/buffering. */
const INBAND_FALLBACK_MS = 8000;

/**
 * Telemetry — emits a normalised TelemetryLatest snapshot via an EventTarget.
 * Consumers subscribe to "update" events; the HUD writes straight into refs
 * without going through any reactive framework (this is the whole point of the
 * 3.0 rebuild — keep the 200 ms tick out of the render graph).
 *
 * As of the 2026-05-20 consolidation, the player reads telemetry exclusively
 * from EXT-X-DATERANGE tags embedded in the HLS playlists. The previous
 * polled fallbacks (directTelemetryServer against connect.race-keeper.com and
 * the deprecated syncedLatestJson on CloudFront) have been removed; the only
 * public surface the player touches is CloudFront.
 *
 * The `TelemetrySource` type is intentionally kept as an open union so a
 * future live-mode push channel (e.g. WebSocket) can plug in without
 * reshaping this API.
 */

export interface TelemetryLatest {
  ok: boolean;
  receivedAtMs: number;
  sourceTimestampMs: number | null;
  speedMph: number | null;
  rpm: number | null;
  throttlePct: number | null;
  fuelPct: number | null;
  coolantC: number | null;
  gpsLat: number | null;
  gpsLon: number | null;
  /** per-field age in seconds, from RK-Telemetry staleness annotations */
  ageS: Record<string, number>;
  staleFields: string[];
  lastSeenAgeS: number | null;
  staleThresholdS: number | null;
}

export interface TelemetryClientOptions {
  loggerId: string;
  source?: TelemetrySource;
  onUpdate?: (snapshot: TelemetryLatest) => void;
  onSourceChange?: (next: TelemetrySource, reason: string) => void;
  /** Optional: returns the wall-clock ms PDT of the currently rendered
   *  video frame. When provided and source==="hlsMetadata", the client
   *  picks the DATERANGE whose START-DATE matches that PDT (frame-
   *  accurate sync) instead of the latest tag (which leads the playing
   *  frame by the live-edge target latency). Step L4 / 2026-06-04. */
  getVideoPdt?: () => number | null;
  /** Returns the in-band ID3 telemetry JSON payload for the playing frame
   *  (from the muxed segments). Used only when source==="hlsInbandId3";
   *  delivered frame-locked by the decoder, so no PDT correlation needed. */
  getInbandTelemetry?: () => string | null;
}

const emptySnapshot = (): TelemetryLatest => ({
  ok: false,
  receivedAtMs: 0,
  sourceTimestampMs: null,
  speedMph: null,
  rpm: null,
  throttlePct: null,
  fuelPct: null,
  coolantC: null,
  gpsLat: null,
  gpsLon: null,
  ageS: {},
  staleFields: [],
  lastSeenAgeS: null,
  staleThresholdS: null,
});

const numFromAny = (raw: any, keys: string[]): number | null => {
  for (const k of keys) {
    const v = pick(raw, k);
    if (v == null) continue;
    const n = typeof v === "number" ? v : parseFloat(String(v));
    if (Number.isFinite(n)) return n;
  }
  return null;
};

function pick(obj: any, dotted: string): any {
  if (!obj) return undefined;
  if (!dotted.includes(".")) return obj[dotted];
  return dotted.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

function parseSnapshot(raw: any, receivedAtMs: number): TelemetryLatest {
  // Server may envelope under `telemetry` (synced JSON) or flatten (direct).
  // Build a fallback chain so numFromAny can look both at root and at the
  // common inner envelopes.
  const env = raw && typeof raw === "object" ? raw : {};
  const inner = (env.telemetry && typeof env.telemetry === "object") ? env.telemetry : {};
  const sync = (env.sync && typeof env.sync === "object") ? env.sync : {};
  const find = (keys: string[]): number | null =>
    numFromAny(env, keys) ?? numFromAny(inner, keys) ?? numFromAny(sync, keys);

  const ts =
    find(["ts_ms", "timestamp_ms", "timestampMs", "ts", "timestamp"]) ?? null;

  // Per-field age annotations (e.g. speed_age_s) — look in root, inner, sync.
  const ageS: Record<string, number> = {};
  for (const bucket of [env, inner, sync]) {
    if (!bucket) continue;
    for (const k of Object.keys(bucket)) {
      if (k.endsWith("_age_s")) {
        const v = parseFloat(String((bucket as any)[k]));
        if (Number.isFinite(v)) ageS[k.replace(/_age_s$/, "")] = v;
      }
    }
  }

  // Staleness threshold may live under sync.stale_sec (synced JSON) OR as
  // stale_threshold_s at root / inside telemetry (direct endpoint).
  const staleThresholdS =
    find(["stale_threshold_s", "staleThresholdS", "stale_sec"]) ?? 10;

  // Fields the SERVER has already judged stale. Hoisted above the metric reads
  // below because those now consult it.
  const staleFields: string[] =
    (Array.isArray(env?.stale_fields) && env.stale_fields) ||
    (Array.isArray(inner?.stale_fields) && inner.stale_fields) ||
    [];
  const staleFieldSet = new Set(staleFields.map(String));

  /** Is the feed still updating this particular field?
   *
   *  The live telemetry endpoint keeps the LAST KNOWN value of every field
   *  indefinitely and annotates each with its own `_age_s`, plus an explicit
   *  `stale_fields` list. A car whose OBD is unplugged therefore keeps
   *  reporting the RPM it had when it was last connected — 764, from seven
   *  weeks earlier, in the case that surfaced this. The device being alive
   *  says nothing about whether an individual sensor still is. */
  const fieldIsStale = (key: string): boolean => {
    if (staleFieldSet.has(key)) return true;
    const age = ageS[key];
    return age != null && age > staleThresholdS;
  };

  /** find(), but yields null for a value the feed is no longer updating.
   *
   *  Checks the age of the KEY THAT SUPPLIED the value, not a guessed name, so
   *  an alias like `speed` vs `speed_mph` is gated on its own annotation.
   *  Sources that carry no per-field ages (the in-band ID3 payload) have an
   *  empty `ageS` and no `stale_fields`, so this degrades to plain find(). */
  const findFresh = (keys: string[]): number | null => {
    for (const bucket of [env, inner, sync]) {
      if (!bucket) continue;
      for (const k of keys) {
        const v = pick(bucket, k);
        if (v == null) continue;
        const n = typeof v === "number" ? v : parseFloat(String(v));
        if (!Number.isFinite(n)) continue;
        return fieldIsStale(k) ? null : n;
      }
    }
    return null;
  };

  // Derive `lastSeenAgeS`. Three fallback paths so EVERY source — direct,
  // synced JSON, AND hlsMetadata — gets a meaningful staleness signal:
  //   1. Server-provided `last_seen_age_s`
  //   2. ISO `telemetry_chosen_ts` (synced JSON) → age = now - parsed
  //   3. Numeric `ts_ms` / `sourceTimestampMs` (hlsMetadata + direct) → same
  // When the device goes offline, the m3u8 stops getting fresh DATERANGE
  // tags but old ones may linger; sourceTimestampMs anchors us correctly.
  const parseIso = (v: any): number | null => {
    if (typeof v !== "string") return null;
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : null;
  };
  const chosenTsMs =
    parseIso(env?.telemetry_chosen_ts) ??
    parseIso(inner?.timestamp) ??
    parseIso(env?.timestamp);
  let lastSeenAgeS = find(["last_seen_age_s", "lastSeenAgeS"]);
  if (lastSeenAgeS == null && chosenTsMs != null) {
    lastSeenAgeS = (Date.now() - chosenTsMs) / 1000;
  }

  const sourceTimestampMs = ts && ts > 1e10 ? ts : ts ? ts * 1000 : null;
  if (lastSeenAgeS == null && sourceTimestampMs != null) {
    lastSeenAgeS = (Date.now() - sourceTimestampMs) / 1000;
  }
  if (lastSeenAgeS != null) lastSeenAgeS = Math.max(0, lastSeenAgeS);

  // NB: an earlier client-side bandaid inflated lastSeenAgeS from
  // sync.offset_sec_telemetry_minus_producer. That workaround was
  // unnecessary once the RK-Video injector (2026-05-17) started gating
  // every field on its own _age_s — stale fields simply never reach the
  // DATERANGE / client now. Server is the right place to enforce that.

  return {
    ok: true,
    receivedAtMs,
    sourceTimestampMs,
    speedMph: findFresh(["speed_mph", "speedMph", "speed", "mph"]),
    rpm: findFresh(["rpm", "engine_rpm", "engineRpm"]),
    throttlePct: findFresh([
      "throttle_pct",
      "throttlePct",
      "throttle",
      "throttle_percent",
      "throttlePercent",
    ]),
    fuelPct: findFresh([
      "fuel_pct",
      "fuelPct",
      "fuel",
      "fuel_percent",
      "fuelPercent",
      // The D700's own OBD-II name, which the Team (live) feed carries as is.
      "fuel_level",
    ]),
    coolantC: findFresh([
      "coolant_c",
      "coolantC",
      "coolant",
      "coolant_temp_c",
      "coolantTempC",
      "coolantTemp",
      // The D700's own OBD-II name, which the Team (live) feed carries as is.
      "coolant_temp",
    ]),
    gpsLat: findFresh(["gps_lat", "gpsLat", "lat", "latitude", "gps.lat"]),
    gpsLon: findFresh(["gps_lon", "gpsLon", "lon", "lng", "longitude", "gps.lon"]),
    ageS,
    staleFields,
    lastSeenAgeS,
    staleThresholdS,
  };
}

/* -----------------------------------------------------------
   HLS metadata reader — pulls EXT-X-DATERANGE from a playlist
   and reconstructs telemetry from custom attributes.

   Returns the FULL list of parsed ranges (each tagged with its
   START-DATE in ms) rather than just the latest. The caller (the
   TelemetryClient) picks the range whose START-DATE matches the
   currently-playing video frame's PDT — so telemetry stays frame-
   aligned with video instead of leading it by ~2 s.
   ----------------------------------------------------------- */
interface ParsedRange {
  startDateMs: number | null;
  attrs: Record<string, string>;
}

async function fetchHlsMetadata(loggerId: string): Promise<ParsedRange[] | null> {
  const text = await fetch(hlsMetadataUrl(loggerId), {
    cache: "no-store",
  }).then((r) => (r.ok ? r.text() : null));
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const ranges: ParsedRange[] = [];
  for (const line of lines) {
    if (!line.startsWith("#EXT-X-DATERANGE:")) continue;
    const attrs: Record<string, string> = {};
    const body = line.slice("#EXT-X-DATERANGE:".length);
    body
      .match(/(?:[^,"]+|"[^"]*")+/g)
      ?.forEach((pair) => {
        const [k, ...rest] = pair.split("=");
        if (!k || rest.length === 0) return;
        let v = rest.join("=").trim();
        if (v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);
        attrs[k.trim()] = v;
      });
    const sd = attrs["START-DATE"];
    const sdMs = sd ? Date.parse(sd) : NaN;
    ranges.push({
      startDateMs: Number.isFinite(sdMs) ? sdMs : null,
      attrs,
    });
  }
  return ranges.length ? ranges : null;
}

/** Convert one parsed DATERANGE entry into the raw telemetry object
 *  that `parseSnapshot` expects. */
function rangeToRawTelemetry(range: ParsedRange): any {
  const get = (k: string) => range.attrs[k];
  const num = (k: string) => {
    const v = get(k);
    if (v == null) return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    ts_ms: range.startDateMs,
    speed_mph: num("X-COM-RACEKEEPER-SPEED"),
    rpm: num("X-COM-RACEKEEPER-RPM"),
    throttle_pct: num("X-COM-RACEKEEPER-THROTTLE"),
    fuel_pct: num("X-COM-RACEKEEPER-FUEL"),
    coolant_c: num("X-COM-RACEKEEPER-COOLANT"),
    gps_lat: num("X-COM-RACEKEEPER-LAT"),
    gps_lon: num("X-COM-RACEKEEPER-LON"),
    // Injector emits the telemetry-sample age at injection time. We prefer
    // this over computing age from START-DATE, because START-DATE carries
    // KVS→HLS pipeline lag (10–20s) that has nothing to do with whether
    // the underlying device is producing fresh telemetry.
    last_seen_age_s: num("X-COM-RACEKEEPER-AGE-S"),
  };
}

/** Find the DATERANGE whose START-DATE is closest to (but not after) the
 *  given target wall-clock ms. Returns null if no range qualifies. */
function pickRangeForPdt(
  ranges: ParsedRange[],
  targetPdtMs: number
): ParsedRange | null {
  let best: ParsedRange | null = null;
  // Small forward tolerance (~one segment) absorbs slight clock skew between
  // device PDT (set by injector from telemetry timestamp) and video PDT.
  const TOLERANCE_MS = 500;
  for (const r of ranges) {
    if (r.startDateMs == null) continue;
    if (r.startDateMs > targetPdtMs + TOLERANCE_MS) continue;
    if (best == null || r.startDateMs > (best.startDateMs ?? -Infinity)) {
      best = r;
    }
  }
  return best;
}

/* -----------------------------------------------------------
   TelemetryClient
   ----------------------------------------------------------- */
export class TelemetryClient extends EventTarget {
  private opts: Required<Pick<TelemetryClientOptions, "loggerId">> & {
    onUpdate?: (s: TelemetryLatest) => void;
    onSourceChange?: (s: TelemetrySource, reason: string) => void;
    getVideoPdt?: () => number | null;
    getInbandTelemetry?: () => string | null;
  };
  private source: TelemetrySource;
  /** HLS polling timer (only running while source === "hlsMetadata"). */
  private hlsTimer: number | null = null;
  /** In-band ID3 pull timer (only running while source === "hlsInbandId3"). */
  private id3Timer: number | null = null;
  /** Fallback bookkeeping: when the in-band source started ticking, and whether
   *  it has ever produced a sample. Drives auto-fallback to DATERANGE when a
   *  logger has no ID3. */
  private id3StartedAt = 0;
  private id3GotData = false;
  /** Live-mode WebSocket (only set while source === "liveWebSocket"). */
  private ws: WebSocket | null = null;
  /** Backoff for WS reconnect (ms, exponential, capped). */
  private wsReconnectMs = 0;
  private wsReconnectTimer: number | null = null;
  private stopped = false;
  /** Wall-clock time of the LAST successful poll/push. Drives staleness. */
  private lastSuccessfulFetchMs = 0;
  public latest: TelemetryLatest = emptySnapshot();

  constructor(opts: TelemetryClientOptions) {
    super();
    this.opts = {
      loggerId: opts.loggerId,
      onUpdate: opts.onUpdate,
      onSourceChange: opts.onSourceChange,
      getVideoPdt: opts.getVideoPdt,
      getInbandTelemetry: opts.getInbandTelemetry,
    };
    this.source = opts.source ?? "hlsMetadata";
  }

  start(): void {
    this.stopped = false;
    this.activateSource();
  }

  /** Force an immediate tick on the HLS poller. No-op in live-WS mode (the
   *  WS itself pushes on every change, there's nothing to poke). */
  poke(): void {
    if (this.stopped) return;
    if (this.source === "hlsMetadata") void this.hlsTick();
  }

  stop(): void {
    this.stopped = true;
    this.teardownHls();
    this.teardownWs();
    this.teardownId3();
  }

  setSource(next: TelemetrySource, reason = "user"): void {
    if (next === this.source) return;
    this.teardownHls();
    this.teardownWs();
    this.teardownId3();
    this.source = next;
    this.opts.onSourceChange?.(next, reason);
    this.dispatchEvent(
      new CustomEvent("sourcechange", { detail: { source: next, reason } })
    );
    if (!this.stopped) this.activateSource();
  }

  getSource(): TelemetrySource {
    return this.source;
  }

  /** Wall-clock age of the latest successful poll/push, in ms. */
  ageMs(): number {
    if (!this.lastSuccessfulFetchMs) return Infinity;
    return Date.now() - this.lastSuccessfulFetchMs;
  }

  /* ----------------- internal: source lifecycle ----------------- */

  private activateSource(): void {
    if (this.source === "hlsMetadata") {
      void this.hlsTick();
      this.hlsTimer = window.setInterval(() => this.hlsTick(), POLL_INTERVAL_MS);
    } else if (this.source === "liveWebSocket") {
      this.openWs();
    } else if (this.source === "hlsInbandId3") {
      this.id3StartedAt = Date.now();
      this.id3GotData = false;
      this.id3Tick();
      // Faster than the network poll (this is a cheap in-memory read of the
      // already-parsed ID3 buffer): a higher rate feeds the map marker smoother
      // interpolated positions, so motion glides instead of stepping.
      this.id3Timer = window.setInterval(() => this.id3Tick(), 80);
    }
  }

  private teardownHls(): void {
    if (this.hlsTimer != null) {
      clearInterval(this.hlsTimer);
      this.hlsTimer = null;
    }
  }

  private teardownId3(): void {
    if (this.id3Timer != null) {
      clearInterval(this.id3Timer);
      this.id3Timer = null;
    }
  }

  private teardownWs(): void {
    if (this.wsReconnectTimer != null) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        this.ws.onopen = null;
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    this.wsReconnectMs = 0;
  }

  /* ----------------- internal: HLS poll path ----------------- */

  private async hlsTick(): Promise<void> {
    if (this.stopped || this.source !== "hlsMetadata") return;
    try {
      const ranges = await fetchHlsMetadata(this.opts.loggerId);
      if (!ranges || !ranges.length) return;

      // Step L4: pick the DATERANGE matching the playing video frame's
      // PDT (frame-accurate sync). Fall back to the latest tag if no PDT
      // callback is wired or it returns null (e.g. video not ready yet).
      const videoPdt = this.opts.getVideoPdt?.() ?? null;
      let chosen: ParsedRange | null = null;
      if (videoPdt != null && Number.isFinite(videoPdt)) {
        chosen = pickRangeForPdt(ranges, videoPdt);
      }
      if (chosen == null) chosen = ranges[ranges.length - 1];

      const raw = rangeToRawTelemetry(chosen);
      if (raw.ts_ms == null) return;
      const snap = parseSnapshot(raw, Date.now());
      this.lastSuccessfulFetchMs = Date.now();
      this.emit(snap);
    } catch {
      /* swallow; staleness is derived from lastSuccessfulFetchMs */
    }
  }

  /* ----------------- internal: in-band ID3 pull path ----------------- */

  /** Pull the in-band telemetry payload for the playing frame and emit it.
   *  No network: the data rides inside the buffered segments and hls.js has
   *  already parsed it, so this is just "read the sample at currentTime".
   *  Frame-locked by construction — no playlist window, no PDT correlation. */
  private id3Tick(): void {
    if (this.stopped || this.source !== "hlsInbandId3") return;
    const text = this.opts.getInbandTelemetry?.() ?? null;
    if (text != null) {
      try {
        const raw = JSON.parse(text);
        const snap = parseSnapshot(raw, Date.now());
        if (snap.sourceTimestampMs != null) {
          this.id3GotData = true;
          this.lastSuccessfulFetchMs = Date.now();
          this.emit(snap);
        }
      } catch {
        /* malformed payload; ignore */
      }
      return;
    }
    // No in-band telemetry for the playing frame. SAFETY FALLBACK: if the video
    // is clearly up but we have NEVER seen an in-band sample within
    // INBAND_FALLBACK_MS, this logger has no ID3 (its box injector isn't
    // running) — switch to the DATERANGE source so it still shows telemetry
    // rather than nothing. Lets us flip the default to in-band fleet-wide while
    // any not-yet-injected logger degrades gracefully instead of going dark.
    if (
      !this.id3GotData &&
      this.id3StartedAt > 0 &&
      (this.opts.getVideoPdt?.() ?? null) != null &&
      Date.now() - this.id3StartedAt > INBAND_FALLBACK_MS
    ) {
      this.setSource("hlsMetadata", "inband-unavailable");
    }
  }

  /* ----------------- internal: WebSocket push path ----------------- */

  private openWs(): void {
    if (this.stopped || this.source !== "liveWebSocket") return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(liveWsUrl(this.opts.loggerId));
    } catch {
      this.scheduleWsReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.wsReconnectMs = 0; // reset backoff on a successful open
    };

    ws.onmessage = (ev) => {
      if (this.source !== "liveWebSocket") return;
      try {
        const raw = JSON.parse(typeof ev.data === "string" ? ev.data : "");
        const snap = parseSnapshot(raw, Date.now());
        this.lastSuccessfulFetchMs = Date.now();
        this.emit(snap);
      } catch {
        /* malformed frame; ignore */
      }
    };

    const onTrouble = () => {
      if (this.stopped) return;
      // Either the socket errored or closed; either way, try to reconnect.
      // Only schedule once per failure — onerror is often followed by onclose.
      if (this.ws === ws) {
        this.ws = null;
        this.scheduleWsReconnect();
      }
    };
    ws.onerror = onTrouble;
    ws.onclose = onTrouble;
  }

  private scheduleWsReconnect(): void {
    if (this.stopped || this.source !== "liveWebSocket") return;
    // Exponential backoff: 500 ms → 1 s → 2 s → 4 s → 8 s (cap).
    this.wsReconnectMs = this.wsReconnectMs ? Math.min(this.wsReconnectMs * 2, 8000) : 500;
    this.wsReconnectTimer = window.setTimeout(() => {
      this.wsReconnectTimer = null;
      this.openWs();
    }, this.wsReconnectMs);
  }

  /* ----------------- internal: emit ----------------- */

  private emit(snap: TelemetryLatest): void {
    this.latest = snap;
    this.opts.onUpdate?.(snap);
    this.dispatchEvent(new CustomEvent("update", { detail: snap }));
  }
}
